import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    Output,
    signal,
    Signal,
    EventEmitter,
    WritableSignal,
    effect,
    ElementRef,
} from '@angular/core';
import { safeMod, uniqueId } from '@co-3/ctrl-ui/common';
import { CtrlIconComponent } from '@co-3/ctrl-ui/icons';
import { Contract } from '@models/contract';
import { Product } from '@models/product';
import { ProductsService } from '../../../products/products.service';

type ContractWithProduct = { contract: Contract; product: Product; index: number };

@Component({
    selector: 'app-contract-select',
    imports: [CtrlIconComponent, NgClass],
    templateUrl: './contract-select.component.html',
    styleUrl: './contract-select.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContractSelectComponent {
    // #region inputs
    public labeledBy = input<string>();
    public contracts = input.required<Contract[]>();
    public currentContract = input.required<Contract>();
    // #endregion

    // #region outputs
    @Output() public selectContract: EventEmitter<Contract> = new EventEmitter<Contract>();
    // #endregion

    // #region injects
    private productsService = inject(ProductsService);
    private elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);
    // #endregion

    private typeaheadQuery = '';
    private resetTypeaheadTimeoutId?: number;

    protected id = uniqueId('contract-select-');

    protected expanded: WritableSignal<boolean> = signal<boolean>(false);

    protected contractsWithProducts: Signal<ContractWithProduct[]> = computed(() =>
        this.contracts().map((contract: Contract, index: number) => {
            const product = this.productsService.getCanonicalByInsuranceType(contract.insuranceType);

            return { product, contract, index };
        }),
    );

    protected currentProduct: Signal<Product> = computed(() =>
        this.productsService.getCanonicalByInsuranceType(this.currentContract().insuranceType),
    );

    protected focusedOption: WritableSignal<number> = signal<number>(-1);

    // #region effects
    private scrollToFocusedOption = effect(() => {
        if (this.focusedOption() === -1) {
            return;
        }
        const focusedOptionElementId = `#${this.id}-option-${this.focusedOption()}`;
        const el = this.elementRef.nativeElement.querySelector(focusedOptionElementId);
        if (el) {
            el.scrollIntoView({
                block: 'nearest',
            });
        }
    });
    // #endregion

    // #region user interaction handling
    protected expand(): void {
        this.expanded.set(true);
    }

    protected collapse(): void {
        this.expanded.set(false);
        this.focusedOption.set(-1);
    }

    protected toggle(): void {
        this.expanded.set(!this.expanded());
        if (this.expanded && this.focusedOption() === -1) {
            this.traverseOptions();
        }
    }

    protected handleButtonKeydown(e: KeyboardEvent): void {
        switch (e.key) {
            case 'Enter':
            case ' ':
                if (this.expanded && this.focusedOption() !== -1) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.selectOption();
                    this.collapse();
                    break;
                }
                break;
            case 'Escape':
                this.collapse();
                break;
            case 'ArrowDown':
                e.preventDefault();
                e.stopPropagation();
                this.expand();
                this.traverseOptions();
                break;
            case 'ArrowUp':
                e.preventDefault();
                e.stopPropagation();
                this.expand();
                this.traverseOptions(-1);
                break;
            case 'Home':
                e.preventDefault();
                e.stopPropagation();
                this.expand();
                this.focusedOption.set(0);
                break;
            case 'End':
                e.preventDefault();
                e.stopPropagation();
                this.expand();
                this.focusedOption.set(this.contracts().length - 1);
                break;
            default:
                this.handleAlphanumericKey(e.key);
                break;
        }
    }

    protected handleOptionMouseDown(e: MouseEvent, optionId: number): void {
        if (e.button === 0) {
            this.selectOption(optionId);
        }
    }

    protected selectOption(optionId?: number): void {
        if (typeof optionId === 'undefined') {
            optionId = this.focusedOption();
        }
        if (optionId !== -1 && this.contracts()[optionId]) {
            this.selectContract.emit(this.contracts()[optionId]);
        }
    }

    private traverseOptions(direction: number = 1): void {
        if (this.focusedOption() < 0) {
            if (direction > 0) {
                this.focusedOption.set(0);
            } else {
                this.focusedOption.set(this.contracts().length - 1);
            }
        } else {
            this.focusedOption.set(safeMod(this.focusedOption() + direction, this.contracts().length));
        }
    }

    /**
     * @description implements the typeahead functionality for the select-only
     * combobox pattern. w3.org (see link below) recommends the following behavior:
     * > - First opens the listbox if it is not already displayed and then moves visual focus to the first option that matches the typed character.
     * > - If multiple keys are typed in quick succession, visual focus moves to the first option that matches the full string.
     * > - If the same character is typed in succession, visual focus cycles among the options starting with that character.
     * This implementation does not handle all printable characters, but only
     * numbers, lower and upper case letters and umlauts.
     * @param key the key pressed
     * @see https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
     */
    private handleAlphanumericKey(key: string): void {
        if (/^[a-zA-Z0-9äüöÄÜÖ]$/.test(key)) {
            this.expand();

            const normalizedKey = key.toLocaleLowerCase();
            if (this.typeaheadQuery !== normalizedKey) {
                this.typeaheadQuery += normalizedKey;
            }
            if (typeof this.resetTypeaheadTimeoutId !== 'undefined') {
                clearTimeout(this.resetTypeaheadTimeoutId);
            }
            this.resetTypeaheadTimeoutId = window.setTimeout(() => (this.typeaheadQuery = ''), 750);

            const matchingContracts = this.contractsWithProducts().filter((contractWithProduct: ContractWithProduct) =>
                contractWithProduct.product.title.toLocaleLowerCase().startsWith(this.typeaheadQuery),
            );

            if (matchingContracts.length) {
                if (this.typeaheadQuery.length === 1) {
                    // find the next matching contract from the current position
                    // if there is none start with the first one. This allows
                    // cycling through all contracts beginning with the same
                    // letter by repeatedly pressing the corresponding key.
                    const nextContract = matchingContracts.find(
                        (contractWithProduct: ContractWithProduct) => contractWithProduct.index > this.focusedOption(),
                    );
                    this.focusedOption.set(nextContract?.index ?? matchingContracts.at(0).index);
                } else {
                    // when the user typed in a query that is matched
                    // by the currently focused option stay there, otherwise
                    // focus the first matching option.
                    if (
                        !matchingContracts.find(
                            (contractWithProduct: ContractWithProduct) =>
                                contractWithProduct.index === this.focusedOption(),
                        )
                    ) {
                        this.focusedOption.set(matchingContracts.at(0).index);
                    }
                }
            }
        }
    }
    // #endregion
}
